import React, { useEffect, useState } from 'react';
import '../GL/LoanLedger.css';
import { maskAadhaar, maskPAN } from '../../utils/sensitiveDataUtils';

export default function TBDLoanLedger() {
  const [noDuesAccounts, setNoDuesAccounts] = useState([]);
  const [duesAccounts, setDuesAccounts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  const [error, setError] = useState('');

  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

  // Function to fetch data from the backend
  useEffect(() => {
    async function fetchLoanData() {
      try {
        const response = await fetch(`${baseUrl}/api/tbd-getLoanLedgerData`);
        const data = await response.json();
        console.log('Fetched TBD loan data:', data);
  
        const summedPayments = data.dailyPayments.reduce((acc, payment) => {
          const { account_no, daily_amount } = payment;
          if (acc[account_no]) {
            acc[account_no] += daily_amount;
          } else {
            acc[account_no] = daily_amount;
          }
          return acc;
        }, {});
  
        const noDues = [];
        const dues = [];
  
        data.tbdLoans.forEach((loan) => {
          const { account_no, loan_amount, period } = loan;
          const totalPaid = summedPayments[account_no] || 0;
          const totalLoanAmount = loan_amount;
          const outstandingAmount = (totalLoanAmount - totalPaid).toFixed(2);
  
          // Ensure accounts with no outstanding amount are placed in the "No Dues" section
          if (parseFloat(outstandingAmount) === 0) {
            noDues.push({ account_no });
          } else {
            dues.push({ account_no, outstandingAmount });
          }
        });
  
        setNoDuesAccounts(noDues);
        setDuesAccounts(dues);
      } catch (error) {
        console.error('Error fetching TBD loan data:', error);
      }
    }
  
    fetchLoanData();
  }, []);

  // Function to go back to the previous page
  const handleBackClick = () => {
    window.history.back();
  };

  async function fetchAccountDetails(accountNo) {
    try {
      setError('');
      setLoading(true);
      setAccountDetails(null);
      const res = await fetch(`${baseUrl}/api/tbd-getAccountDetails/${encodeURIComponent(accountNo)}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Failed to fetch account details');
      }
      const details = await res.json();
      if (!details || !details.loan) {
        throw new Error('Invalid data received');
      }
      setAccountDetails(details);
    } catch (e) {
      console.error(e);
      setError('Unable to load account details. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  const openDialog = (accountNo) => {
    setSelectedAccount(accountNo);
    setDialogOpen(true);
    fetchAccountDetails(accountNo);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
    setAccountDetails(null);
    setError('');
    setLoading(false);
  };
  
  return (
    <div style={{ padding: '20px' }}>
     {/* Back Button */}
      <button onClick={handleBackClick} className="back-button">
        Back
      </button>

      {/* No Dues Section */}
      <div style={{ padding: '10px', border: '1px solid black', margin: '10px' }}>
        <h2>No Dues Accounts</h2>
        {noDuesAccounts.length === 0 ? (
          <p>No accounts with no dues.</p>
        ) : (
          <ul>
            {noDuesAccounts.map((account) => (
              <li key={account.account_no}>
                <span
                  className="clickable-account"
                  onClick={() => openDialog(account.account_no)}
                >
                  {account.account_no}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Dues Section */}
      <div style={{ padding: '10px', border: '1px solid black', margin: '10px' }}>
        <h2>Accounts with Dues</h2>
        {duesAccounts.length === 0 ? (
          <p>No accounts with pending dues.</p>
        ) : (
          <ul>
            {duesAccounts.map((account) => (
              <li key={account.account_no}>
                <span
                  className="clickable-account"
                  onClick={() => openDialog(account.account_no)}
                >
                  {account.account_no}
                </span>
                <span className="outstanding">₹{account.outstandingAmount}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {dialogOpen && (
        <div className="account-dialog-overlay" onClick={closeDialog}>
          <div className="account-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <div className="dialog-title">
                TBD Account Details {selectedAccount ? `- ${selectedAccount}` : ''}
              </div>
              <button className="dialog-close" onClick={closeDialog}>×</button>
            </div>

            {loading && (
              <div className="loading-state">
                <div className="spinner" />
                <div>Loading account details...</div>
              </div>
            )}

            {!loading && error && (
              <div className="error-state">{error}</div>
            )}

            {!loading && !error && accountDetails && (
              <div className="dialog-content">
                {/* Summary */}
                <div className="summary-cards">
                  <div className="card">
                    <div className="card-label">Total Loan</div>
                    <div className="card-value">₹{Number(accountDetails?.summary?.total_loan_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="card">
                    <div className="card-label">Total Paid</div>
                    <div className="card-value">₹{Number(accountDetails?.summary?.total_paid_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="card">
                    <div className="card-label">Outstanding</div>
                    <div className="card-value">₹{Number(accountDetails?.summary?.total_remaining_amount || 0).toFixed(2)}</div>
                  </div>
                  <div className="card">
                    <div className="card-label">Paid Days</div>
                    <div className="card-value">{Number(accountDetails?.summary?.paid_days || 0)} / {Number(accountDetails?.summary?.total_days || 0)}</div>
                  </div>
                </div>

                {/* Loan Info */}
                <div className="info-grid">
                  <div className="info-section">
                    <div className="section-title">TBD Loan Information</div>
                    <div className="info-row"><span>Account</span><strong>{accountDetails?.loan?.account_no || 'N/A'}</strong></div>
                    <div className="info-row"><span>Date</span><strong>{accountDetails?.loan?.date || 'N/A'}</strong></div>
                    <div className="info-row"><span>Amount</span><strong>₹{Number(accountDetails?.loan?.loan_amount || 0).toFixed(2)}</strong></div>
                    <div className="info-row"><span>Period (days)</span><strong>{accountDetails?.loan?.period || 'N/A'}</strong></div>
                    <div className="info-row"><span>Daily Collection</span><strong>₹{Number(accountDetails?.loan?.daily_collection || 0).toFixed(2)}</strong></div>
                    <div className="info-row"><span>Collector Name</span><strong>{accountDetails?.loan?.collector_name || 'N/A'}</strong></div>
                    <div className="info-row"><span>Recommended By</span><strong>{accountDetails?.loan?.recommended_by || 'N/A'}</strong></div>
                  </div>

                  <div className="info-section">
                    <div className="section-title">Debtor Information</div>
                    <div className="info-row"><span>Name</span><strong>{accountDetails?.debtor?.name || 'N/A'}</strong></div>
                    <div className="info-row"><span>Aadhaar</span><strong>{maskAadhaar(accountDetails?.debtor?.aadhaar) || 'N/A'}</strong></div>
                    <div className="info-row"><span>PAN</span><strong>{maskPAN(accountDetails?.debtor?.pan) || 'N/A'}</strong></div>
                    <div className="info-row"><span>Phone</span><strong>{accountDetails?.debtor?.phone || 'N/A'}</strong></div>
                    <div className="info-row"><span>Address</span><strong>{accountDetails?.debtor?.address || 'N/A'}</strong></div>
                    <div className="photo-box">
                      {accountDetails?.debtor?.photo ? (
                        <img src={accountDetails?.debtor?.photo} alt="Debtor" />
                      ) : (
                        <div className="no-photo">No Photo</div>
                      )}
                    </div>
                  </div>

                  <div className="info-section">
                    <div className="section-title">Guarantor Information</div>
                    <div className="info-row"><span>Name</span><strong>{accountDetails?.guarantor?.name || 'N/A'}</strong></div>
                    <div className="info-row"><span>Aadhaar</span><strong>{maskAadhaar(accountDetails?.guarantor?.aadhaar) || 'N/A'}</strong></div>
                    <div className="info-row"><span>Phone</span><strong>{accountDetails?.guarantor?.phone || 'N/A'}</strong></div>
                    <div className="info-row"><span>Address</span><strong>{accountDetails?.guarantor?.address || 'N/A'}</strong></div>
                    <div className="info-row"><span>Second Guarantor</span><strong>{accountDetails?.guarantor?.second_guarantor || 'N/A'}</strong></div>
                    <div className="photo-box">
                      {accountDetails?.guarantor?.photo ? (
                        <img src={accountDetails?.guarantor?.photo} alt="Guarantor" />
                      ) : (
                        <div className="no-photo">No Photo</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Daily Collection Status */}
                <div className="table-section">
                  <div className="section-title">Daily Collection Status</div>
                  <div className="table-wrap">
                    <table className="details-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Daily Amount</th>
                          <th>Paid</th>
                          <th>Status</th>
                          <th>Days Overdue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(accountDetails?.daily_collections) && accountDetails?.daily_collections?.length > 0 ? (
                          accountDetails?.daily_collections?.map((d, idx) => (
                            <tr key={`${d?.date || idx}`} className={d?.is_overdue && !d?.is_paid ? 'row-overdue' : ''}>
                              <td>{d?.date || 'N/A'}</td>
                              <td>₹{Number(d?.daily_amount || 0).toFixed(2)}</td>
                              <td>{d?.is_paid ? 'Yes' : 'No'}</td>
                              <td>
                                {d?.is_paid ? (
                                  <span className="badge badge-ok">Paid</span>
                                ) : (
                                  <span className="badge badge-overdue">Pending</span>
                                )}
                              </td>
                              <td>{Number(d?.days_overdue || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={5} style={{ textAlign: 'center' }}>No daily collection data available</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Transactions */}
                <div className="table-section">
                  <div className="section-title">Transaction History</div>
                  <div className="table-wrap">
                    <table className="details-table">
                      <thead>
                        <tr>
                          <th>Payment Date</th>
                          <th>Amount</th>
                          <th>Mode</th>
                          <th>Transaction ID</th>
                          <th>Collection Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.isArray(accountDetails?.transactions) && accountDetails?.transactions?.length > 0 ? (
                          accountDetails?.transactions?.map((t, idx) => (
                            <tr key={`${t?.payment_date || idx}-${t?.transaction_id || idx}`}>
                              <td>{t?.payment_date || 'N/A'}</td>
                              <td>₹{Number(t?.amount || 0).toFixed(2)}</td>
                              <td>{t?.payment_mode || 'N/A'}</td>
                              <td>{t?.transaction_id || 'N/A'}</td>
                              <td>{t?.collection_date || 'N/A'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan={5} style={{ textAlign: 'center' }}>No transactions found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
